import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import BottomNav from '../components/BottomNav';

const API_BASE = process.env.REACT_APP_API_URL;

const dailyQuotes = [
  { text: "Every day is a new beginning. Take a deep breath and start again.", author: "Unknown" },
  { text: "Age is merely the number of years the world has been enjoying you.", author: "Unknown" },
  { text: "You don't stop laughing when you grow old, you grow old when you stop laughing.", author: "George Bernard Shaw" },
  { text: "The best time to plant a tree was 20 years ago. The second best time is now.", author: "Chinese Proverb" },
  { text: "Happiness is not something ready-made. It comes from your own actions.", author: "Dalai Lama" },
  { text: "जीवन में कठिनाइयाँ हमें बर्बाद करने नहीं आतीं, बल्कि हमें अपनी असली ताकत दिखाने आती हैं।", author: "Unknown" },
  { text: "Count your joys instead of your woes. Count your friends instead of your foes.", author: "Unknown" }
];

const quickActions = [
  { label: "Today's Walk", emoji: '🏃', color: '#4CAF50', bg: '#E8F5E9', path: '/events' },
  { label: 'Community Chat', emoji: '💬', color: '#2196F3', bg: '#E3F2FD', path: '/community' },
  { label: 'Events Nearby', emoji: '📅', color: '#9C27B0', bg: '#F3E5F5', path: '/events' },
  { label: 'Request Help', emoji: '🤝', color: '#FF6B35', bg: '#FFF3EE', path: '/services' }
];

const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return { text: 'Good Morning', emoji: '🌞' };
  if (hour < 17) return { text: 'Good Afternoon', emoji: '☀️' };
  return { text: 'Good Evening', emoji: '🌙' };
};

const getInitials = (name) => {
  if (!name) return '?';
  return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
};

const formatTimeAgo = (dateStr) => {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
};

const Home = () => {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [posts, setPosts] = useState([]);
  const [postLoading, setPostLoading] = useState(true);
  const [newPost, setNewPost] = useState('');
  const [posting, setPosting] = useState(false);
  const [showPostBox, setShowPostBox] = useState(false);

  const greeting = getGreeting();
  const quote = dailyQuotes[new Date().getDay() % dailyQuotes.length];

  const headers = { Authorization: `Bearer ${token}` };

  const fetchFeed = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/api/feed`, { headers });
      setPosts(res.data.posts || []);
    } catch (err) {
      console.error('Feed fetch error:', err);
    } finally {
      setPostLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchFeed();
  }, [fetchFeed]);

  const handlePost = async () => {
    if (!newPost.trim()) return;
    setPosting(true);
    try {
      const res = await axios.post(`${API_BASE}/api/feed`, { content: newPost }, { headers });
      setPosts([res.data.post, ...posts]);
      setNewPost('');
      setShowPostBox(false);
      toast.success('Post shared with the community!');
    } catch (err) {
      toast.error('Failed to post. Try again.');
    } finally {
      setPosting(false);
    }
  };

  const handleLike = async (postId, liked) => {
    try {
      await axios.post(`${API_BASE}/api/feed/${postId}/like`, {}, { headers });
      setPosts(posts.map(p =>
        p.id === postId
          ? { ...p, liked_by_me: !liked, like_count: liked ? p.like_count - 1 : p.like_count + 1 }
          : p
      ));
    } catch (err) {
      toast.error('Failed to update like.');
    }
  };

  return (
    <div className="page-container">
      {/* Top bar */}
      <div style={{
        background: 'linear-gradient(135deg, #FF6B35 0%, #FF8C42 100%)',
        padding: '20px 16px 24px',
        color: 'white'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <p style={{ fontSize: '14px', opacity: 0.85, marginBottom: '2px' }}>
              {greeting.emoji} {greeting.text}
            </p>
            <h1 style={{ color: 'white', fontSize: '24px', fontWeight: '700' }}>
              {user?.name?.split(' ')[0] || 'Friend'}!
            </h1>
          </div>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
            <button
              onClick={() => navigate('/notifications')}
              style={{ background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: '50%', width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', fontSize: '20px' }}
            >
              🔔
            </button>
            <div
              className="avatar"
              style={{ background: 'rgba(255,255,255,0.3)', border: '2px solid white', cursor: 'pointer' }}
              onClick={() => navigate('/profile')}
            >
              {getInitials(user?.name)}
            </div>
          </div>
        </div>

        {/* Daily quote card */}
        <div style={{
          background: 'rgba(255,255,255,0.15)',
          borderRadius: '12px',
          padding: '12px 14px',
          marginTop: '16px',
          backdropFilter: 'blur(10px)'
        }}>
          <p style={{ fontSize: '13px', color: 'rgba(255,255,255,0.8)', marginBottom: '4px' }}>
            ✨ Quote of the Day
          </p>
          <p style={{ fontSize: '14px', color: 'white', fontWeight: '500', lineHeight: '1.5' }}>
            "{quote.text}"
          </p>
          {quote.author !== 'Unknown' && (
            <p style={{ fontSize: '12px', color: 'rgba(255,255,255,0.7)', marginTop: '4px' }}>
              — {quote.author}
            </p>
          )}
        </div>
      </div>

      <div className="page-content" style={{ paddingTop: '16px' }}>
        {/* Quick Actions */}
        <div style={{ marginBottom: '20px' }}>
          <h3 style={{ marginBottom: '12px' }}>Quick Actions</h3>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
            {quickActions.map((action) => (
              <button
                key={action.label}
                onClick={() => navigate(action.path)}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-start',
                  padding: '16px',
                  background: action.bg,
                  border: `2px solid ${action.color}20`,
                  borderRadius: '16px',
                  cursor: 'pointer',
                  textAlign: 'left',
                  fontFamily: "'Hind', 'Poppins', sans-serif",
                  transition: 'all 0.2s'
                }}
              >
                <span style={{ fontSize: '28px', marginBottom: '8px' }}>{action.emoji}</span>
                <span style={{ fontSize: '14px', fontWeight: '700', color: action.color }}>
                  {action.label}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Health & SOS Quick Row */}
        <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
          <button
            onClick={() => navigate('/health')}
            style={{
              flex: 1,
              padding: '14px',
              background: '#E8F5E9',
              border: '2px solid #4CAF5020',
              borderRadius: '14px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              fontFamily: "'Hind', 'Poppins', sans-serif"
            }}
          >
            <span style={{ fontSize: '24px' }}>❤️</span>
            <div style={{ textAlign: 'left' }}>
              <div style={{ fontSize: '13px', fontWeight: '700', color: '#388E3C' }}>Log Health</div>
              <div style={{ fontSize: '11px', color: '#666' }}>Daily tracking</div>
            </div>
          </button>
          <button
            onClick={() => navigate('/sos')}
            style={{
              flex: 1,
              padding: '14px',
              background: '#FFEBEE',
              border: '2px solid #F4433620',
              borderRadius: '14px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              fontFamily: "'Hind', 'Poppins', sans-serif"
            }}
          >
            <span style={{ fontSize: '24px' }}>🆘</span>
            <div style={{ textAlign: 'left' }}>
              <div style={{ fontSize: '13px', fontWeight: '700', color: '#D32F2F' }}>SOS Alert</div>
              <div style={{ fontSize: '11px', color: '#666' }}>Emergency help</div>
            </div>
          </button>
        </div>

        {/* Community Feed */}
        <div style={{ marginBottom: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <h3>Community Feed</h3>
            <button
              onClick={() => setShowPostBox(!showPostBox)}
              style={{
                background: '#FF6B35',
                color: 'white',
                border: 'none',
                borderRadius: '20px',
                padding: '8px 16px',
                fontSize: '14px',
                fontWeight: '600',
                cursor: 'pointer',
                fontFamily: "'Hind', 'Poppins', sans-serif"
              }}
            >
              + Share
            </button>
          </div>

          {/* New Post Box */}
          {showPostBox && (
            <div className="card" style={{ marginBottom: '12px' }}>
              <div style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                <div className="avatar" style={{ background: '#FF6B35' }}>
                  {getInitials(user?.name)}
                </div>
                <textarea
                  className="form-input"
                  placeholder="Share something with the community... 😊"
                  value={newPost}
                  onChange={(e) => setNewPost(e.target.value)}
                  rows={3}
                  style={{ resize: 'none', flex: 1, minHeight: '80px' }}
                  autoFocus
                />
              </div>
              <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                <button className="btn btn-ghost btn-sm" style={{ width: 'auto' }} onClick={() => { setShowPostBox(false); setNewPost(''); }}>
                  Cancel
                </button>
                <button className="btn btn-primary btn-sm" style={{ width: 'auto' }} onClick={handlePost} disabled={posting || !newPost.trim()}>
                  {posting ? 'Posting...' : '📤 Post'}
                </button>
              </div>
            </div>
          )}

          {/* Posts */}
          {postLoading ? (
            <div className="loading-spinner">
              <div className="spinner"></div>
            </div>
          ) : posts.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">🌻</div>
              <div className="empty-title">No posts yet</div>
              <div className="empty-text">Be the first to share something!</div>
            </div>
          ) : (
            posts.slice(0, 5).map((post) => (
              <div key={post.id} className="card">
                <div style={{ display: 'flex', gap: '10px', marginBottom: '10px', alignItems: 'flex-start' }}>
                  <div className="avatar" style={{ background: post.user_id === user?.id ? '#FF6B35' : '#9C27B0' }}>
                    {getInitials(post.user_name)}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontWeight: '700', fontSize: '15px' }}>{post.user_name}</span>
                      <span style={{ fontSize: '12px', color: '#999' }}>{formatTimeAgo(post.created_at)}</span>
                    </div>
                    <span className="badge badge-primary" style={{ marginTop: '2px', fontSize: '11px' }}>
                      {post.member_type}
                    </span>
                  </div>
                </div>
                <p style={{ fontSize: '16px', lineHeight: '1.6', color: '#2D2D2D', marginBottom: '10px' }}>
                  {post.content}
                </p>
                <div style={{ display: 'flex', gap: '12px', borderTop: '1px solid #F0E8E0', paddingTop: '8px' }}>
                  <button
                    onClick={() => handleLike(post.id, post.liked_by_me)}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px',
                      fontSize: '15px',
                      color: post.liked_by_me ? '#FF6B35' : '#999',
                      fontWeight: post.liked_by_me ? '700' : '400',
                      fontFamily: "'Hind', 'Poppins', sans-serif",
                      padding: '4px 8px'
                    }}
                  >
                    {post.liked_by_me ? '❤️' : '🤍'} {post.like_count}
                  </button>
                </div>
              </div>
            ))
          )}

          {posts.length > 5 && (
            <button className="btn btn-ghost" onClick={() => navigate('/community')} style={{ marginTop: '4px' }}>
              View all posts in Community →
            </button>
          )}
        </div>
      </div>

      {/* Floating SOS Button */}
      <button className="fab-sos" onClick={() => navigate('/sos')} title="SOS Emergency">
        🆘
      </button>

      <BottomNav />
    </div>
  );
};

export default Home;
